import { cn, formatNumber } from '@/app/lib/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import TagsInput from './TagsInput';
import CustomSelect, { Option } from './CustomSelect';
import OutsideClickListener from './OutsideClickListener';
import DatePicker from './DatePicker';
import Input from './Input';
import Textarea from './Textarea';
import UserSelect from '../common/UserSelect';

type CommonProps = {
  className?: string;
  validation?: string[];
  placeholder?: string;
  inputClassName?: string;
  inputWrapperClassName?: string;
  isTableCell?: boolean;
  isInfoTable?: boolean;
  initialEditing?: boolean;
  prefixWrapperClassName?: string;
  characterLimit?: number;
  onEditingChange?: (isEditing: boolean, value?: string | string[] | number) => void;
};

interface TagsInputProps {
  initialValue?: string[];
  onSave: (value: string[]) => void;
  type: 'tags';
  subType?: 'text' | 'number';
  prefixIcon?: React.ReactNode;
}

type TextInputProps = {
  initialValue: string;
  onSave: (value: string) => void;
  type: 'text';
  prefixIcon?: React.ReactNode;
  displayValue?: string;
};

type TimeInputProps = {
  initialValue: string;
  onSave: (value: string) => void;
  type: 'time';
  prefixIcon?: React.ReactNode;
};

type NumberInputProps = {
  initialValue: number | undefined;
  onSave: (value: number) => void;
  type: 'number';
  shouldFormat?: boolean;
  prefixIcon?: React.ReactNode;
};

type TextareaInputProps = {
  initialValue: string;
  onSave: (value: string) => void;
  type: 'textarea';
};

type SelectInputProps = {
  initialValue: string | string[];
  onSave: (v: string | string[] | undefined) => void;
  type: 'select';
  options: Option[];
  multiple?: boolean;
  maxTags?: number;
  searchable?: boolean;
};

type UserSelectInputProps = {
  initialValue: string | string[];
  onSave: (v: string | string[] | undefined) => void;
  type: 'user-select';
};

type DateInputProps = {
  initialValue: string;
  onSave: (value: Date | undefined) => void;
  type: 'date';
  dateFormat?: string;
  subType?: 'date' | 'datetime';
  disablePastDates?: boolean;
};

type Props = CommonProps &
  (
    | TagsInputProps
    | TextInputProps
    | SelectInputProps
    | DateInputProps
    | TextareaInputProps
    | NumberInputProps
    | UserSelectInputProps
    | TimeInputProps
  );

const EditableText = (props: Props) => {
  const {
    initialValue,
    className,
    type,
    inputClassName,
    inputWrapperClassName,
    prefixWrapperClassName,
    isTableCell = false,
    isInfoTable = false,
    initialEditing = false,
  } = props;
  const [value, setValue] = useState<string | string[] | number | undefined>(
    type === 'number' ? (initialValue !== 0 ? initialValue : undefined) : initialValue
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(initialEditing);
  const isRequired = props.validation?.includes('required');
  const maxLength = props.validation?.find(rule => rule.startsWith('maxLength'))?.split('|')[1];
  const inputRef = useRef<HTMLInputElement>(null);

  const maxLengthNumber = useMemo(() => {
    if (!maxLength) return undefined;
    return parseInt(maxLength);
  }, [maxLength]);

  const handleSave = () => {
    if (isRequired && (!value || value === '')) {
      setValue(initialValue);
      setIsEditing(false);
      props.onEditingChange?.(false, initialValue);
      return;
    }

    if (!props.onSave || props.type === 'select' || props.type === 'date') return;

    if (type === 'text' || type === 'textarea' || type === 'time') {
      if (value !== initialValue) {
        props.onSave(value as string);
      }
    }
    if (type === 'number' && value !== initialValue) {
      const number = Number(value);
      if (!isNaN(number)) {
        props.onSave(number);
      }
    }
    setIsEditing(false);
    props.onEditingChange?.(false, value);
  };
  useEffect(() => {
    if (type === 'number') {
      setValue(initialValue !== 0 ? initialValue : undefined);
    } else {
      setValue(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    if (initialEditing) inputRef.current?.focus();
  }, [initialEditing]);

  // if (isTableCell && !isEditing) {
  //   return (
  //     <div
  //       className="p-2 cursor-pointer w-full h-full flex items-center justify-center"
  //       onDoubleClick={() => setIsEditing(true)}
  //     >
  //       {value}
  //     </div>
  //   );
  // }

  return (
    <div
      // contentEditable={isEditing && props.type === 'text'}
      // onBlur={handleSave}
      // onKeyDown={e => {
      //   if (e.key === 'Enter') {
      //     handleSave(e as unknown as React.FocusEvent<HTMLDivElement>);
      //   }
      // }}
      ref={contentRef}
      // onMouseEnter={() => setIsEditing(true)}
      // onInput={() => setIsChanged(true)}
      // onClick={() => setIsChanged(true)}
      // onMouseLeave={() => !isChanged && setIsEditing(false)}
      className={cn(
        'border-transparent border rounded-sm outline-none px-2',
        !isInfoTable && '-mx-2',
        (type === 'text' ||
          type === 'tags' ||
          type === 'textarea' ||
          type === 'number' ||
          type === 'time') &&
          !isTableCell &&
          'hover:border-border',
        isEditing &&
          (type === 'text' ||
            type === 'textarea' ||
            type === 'tags' ||
            type === 'number' ||
            type === 'time') &&
          (!isTableCell
            ? 'border border-border rounded-sm min-w-0'
            : 'border border-blue-base rounded-none'),
        (type === 'textarea' ||
          type === 'tags' ||
          (isInfoTable && type !== 'select' && type !== 'user-select')) &&
          'w-full min-w-0 max-w-full',
        isTableCell && 'p-0 mx-0 w-full h-full',
        isInfoTable &&
          !isTableCell &&
          type !== 'select' &&
          type !== 'user-select' &&
          'min-w-0 w-full max-w-full overflow-hidden',
        isInfoTable &&
          !isTableCell &&
          (type === 'select' || type === 'user-select') &&
          'w-fit max-w-full',
        className
      )}
      suppressContentEditableWarning={true}
    >
      {(type === 'text' || type === 'number' || type === 'time') && (
        <Input
          value={
            type === 'number' && !isEditing && (props as NumberInputProps).shouldFormat !== false
              ? formatNumber(value as number)
              : type === 'text' &&
                  !isEditing &&
                  (props as TextInputProps).displayValue !== undefined
                ? (props as TextInputProps).displayValue!
                : (value as string)
          }
          onChange={e => {
            if (maxLengthNumber && `${e.target.value}`.length > maxLengthNumber) {
              return;
            }
            setValue(e.target.value);
          }}
          ref={inputRef}
          prefix={props.prefixIcon}
          type={type === 'number' && !isEditing ? 'text' : type}
          onBlur={handleSave}
          onFocus={() => {
            setIsEditing(true);
            props.onEditingChange?.(true, value);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              handleSave();
              inputRef.current?.blur();
            }
          }}
          className={cn(
            'border-0 px-0 py-0 h-fit w-full min-w-0 max-w-full truncate',
            type === 'time' && 'min-w-[85px]',
            isTableCell && 'w-full h-full bg-transparent px-3',
            inputClassName
          )}
          wrapperClassName={cn(
            '!border-0 px-0 !bg-transparent focus-within:!border-0 focus-within:!outline-none focus-within:!ring-0 focus-within:!ring-offset-0 w-full min-w-0 max-w-full !h-auto !min-h-0 overflow-hidden',
            isTableCell && 'w-full h-full bg-transparent',
            !isTableCell && 'bg-transparent',
            inputWrapperClassName
          )}
          prefixWrapperClassName={prefixWrapperClassName}
          placeholder={props.placeholder}
        />
      )}

      {type === 'textarea' && (
        <Textarea
          className="border-0 bg-transparent px-0 py-0 w-full min-w-0 max-w-full h-auto"
          wrapperClassName="!border-0 !bg-transparent px-0 py-1 focus-within:!border-0 focus-within:!outline-none focus-within:!ring-0 focus-within:!ring-offset-0 w-full min-w-0 max-w-full"
          placeholder={props.placeholder}
          rows={8}
          value={value as string}
          characterLimit={props.characterLimit}
          onChange={e => {
            setValue(e.target.value);
          }}
          onBlur={handleSave}
          onFocus={() => {
            setIsEditing(true);
            props.onEditingChange?.(true, value);
          }}
        />
      )}

      {type === 'tags' && (
        <div className="flex w-full min-w-0 flex-wrap gap-1">
          <TagsInput
            wrapperClassName={cn(
              'w-full min-w-0 !border-0 !bg-transparent px-0 focus-within:!border-0 focus-within:!outline-none focus-within:!ring-0 focus-within:!ring-offset-0',
              isInfoTable && '!min-h-0 !py-0'
            )}
            initialTags={(value || []) as string[]}
            onTagsChange={props.onSave}
            type={props.subType}
            inputProps={{
              wrapperClassName: '!min-w-0',
              placeholder: props.placeholder,
              className: 'px-0 min-w-0 w-full',
              onFocus: () => {
                setIsEditing(true);
                props.onEditingChange?.(true, value);
              },
              onBlur: () => {
                setIsEditing(false);
                props.onEditingChange?.(false, value);
              },
            }}
          />
        </div>
      )}
      {type === 'select' && (
        <div onClick={e => e.stopPropagation()} className={isInfoTable ? 'w-fit' : undefined}>
          <OutsideClickListener
            onOutsideClick={() => {
              setIsEditing(false);
            }}
          >
            <CustomSelect
              options={props.options}
              defaultValue={value as string}
              triggerIcon="arrow-down-s-fill"
              triggerIconClassNames="text-foreground"
              varient={props.multiple ? 'inline-multiple' : 'default'}
              searchable={props.searchable}
              onOptionSelect={v => {
                setIsEditing(false);
                if (props.multiple) {
                  const value = (v as Option[]).map(o => o.id.toString());
                  setValue(value);
                  props.onSave(value);
                } else {
                  const value = (v as Option).id.toString();
                  setValue(value);
                  props.onSave(value);
                }
              }}
              multiple={props.multiple}
              maxTags={props.maxTags}
              wrapperClassNames={isInfoTable ? '!w-fit' : undefined}
              triggerClassNames={cn(
                'border-0 !pl-0',
                isInfoTable && '!h-[22px] !min-h-[22px] !py-0 !w-fit'
              )}
              triggerWrapperClassName={isInfoTable ? '!w-fit gap-1.5' : undefined}
            />
          </OutsideClickListener>
        </div>
      )}

      {type === 'user-select' && (
        <div onClick={e => e.stopPropagation()} className={isInfoTable ? 'w-fit' : undefined}>
          <OutsideClickListener
            onOutsideClick={() => {
              setIsEditing(false);
            }}
          >
            <UserSelect
              searchable
              defaultValue={value as string}
              triggerIcon="arrow-down-s-fill"
              triggerIconClassNames="text-foreground"
              onOptionSelect={v => {
                const value = (v as Option).id.toString();
                setValue(value);
                props.onSave(value);
              }}
              triggerClassNames={cn(
                'border-0 !pl-0',
                isInfoTable && '!h-[22px] !min-h-[22px] !py-0'
              )}
            />
          </OutsideClickListener>
        </div>
      )}

      {type === 'date' && (
        <div onClick={e => e.stopPropagation()}>
          <OutsideClickListener
            onOutsideClick={() => {
              setIsEditing(false);
            }}
          >
            <DatePicker
              applied={value && value !== '-' ? new Date(value as string) : undefined}
              onApply={date => {
                console.log('date', date);
                setValue(date?.toString() || '');
                props.onSave((date as Date) || undefined);
                setIsEditing(false);
              }}
              showTimePicker={props.subType === 'datetime'}
              showClearButton={false}
              iconName="arrow-down-s-fill"
              dateFormat={props.dateFormat || 'MMM dd, yyyy, hh:mm a'}
              triggerClassNames="border-0 px-0 hover:bg-transparent font-normal gap-1 py-0 h-fit"
              iconClassName="text-foreground"
              mode="single"
              disablePastDates={props.disablePastDates}
            />
          </OutsideClickListener>
        </div>
      )}
    </div>
  );
};

export default EditableText;
